import { search } from 'jmespath'
import { isObject } from './isObject'
import { getFlatObjectKeyFromJMESPath } from './getFlatObjectKeyFromJMESPath'
import { deepClone } from './deepClone'

export type ResolveStrategy =
  | 'replace'
  | 'replacePreferPatch'
  | 'replacePreferBase'
  | 'mergePreferPatch'
  | 'mergePreferBase'

function filterIgnoredValues(
  target: Record<string, unknown>,
  mergeIgnoreValues: any[],
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(target).filter(
      ([_, value]) => !mergeIgnoreValues.includes(value),
    ),
  )
}

export function changeObjectPropertyByJmespath<T extends Object>(
  obj: T,
  jmespath: string,
  valueToChange: unknown,
  options?: {
    resolveStrategy?: ResolveStrategy
    snippetAllowedFields?: string[]
    mergeIgnoreValues?: (string | number | boolean | null)[]
  },
) {
  const {
    resolveStrategy: givenResolveStrategy = 'replace', // equivalent to replacePreferPatch
    snippetAllowedFields,
    mergeIgnoreValues = [null, ''], // fallback values (api vs frontend)
  } = options ?? {}

  let resolveStrategy =
    givenResolveStrategy !== 'replace'
      ? givenResolveStrategy
      : 'replacePreferPatch'

  let internalObj = { ...obj }

  if (Object.isFrozen(obj)) {
    internalObj = deepClone(obj)
  }

  const flatKey = getFlatObjectKeyFromJMESPath({ data: internalObj, jmespath })

  const searchFlatKey = flatKey.replaceAll(
    /((?!\/--)[^.]*-(?!-)[^.]*)/g,
    '"$1"',
  )

  if (resolveStrategy === 'replacePreferPatch') {
    // continue processing
  }


  if (resolveStrategy === 'replacePreferBase') {
    const currentValue = search(internalObj, searchFlatKey)

    if (!mergeIgnoreValues.includes(currentValue as any)) {
      return internalObj
    }
  }

  let targetValue = valueToChange

  if (isObject(targetValue)) {
    const currentValue = search(internalObj, searchFlatKey)

    if (
      isObject(currentValue) &&
      (resolveStrategy === 'mergePreferPatch' ||
        resolveStrategy === 'mergePreferBase')
    ) {
      const currentValueCopy = { ...currentValue }

      if (snippetAllowedFields) {
        snippetAllowedFields.forEach((snippetField) => {
          delete currentValueCopy[snippetField]
        })
      }

      if (resolveStrategy === 'mergePreferPatch') {
        targetValue = {
          ...currentValueCopy,
          ...filterIgnoredValues(
            targetValue as Record<string, unknown>,
            mergeIgnoreValues,
          ),
        }
      } else if (resolveStrategy === 'mergePreferBase') {
        targetValue = {
          ...filterIgnoredValues(
            targetValue as Record<string, unknown>,
            mergeIgnoreValues,
          ),
          ...filterIgnoredValues(currentValueCopy, mergeIgnoreValues),
        }
      }
    }
  }

  const evalFlatKey = flatKey.replaceAll(
    /\.((?!\/--)[^.]*-(?!-)[^.]*)/g,
    '["$1"]',
  )

  const setPropertyFunction = new Function(
    'obj',
    'path',
    'value',
    `obj.${evalFlatKey} = value;`,
  )

  try {
    setPropertyFunction(internalObj, flatKey, targetValue)
  } catch (e: any) {
    console.error(`[changeObjectPropertyByJmespath]: ${e.message}`)
    throw new Error(`[changeObjectPropertyByJmespath]: ${e.message}`)
  }

  return internalObj
}
